import { useEffect } from "react";
import { useNavigate } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  useGetApiTenantsTenantId,
  usePatchApiTenantsTenantId,
} from "@/lib/api/generated/tenants/tenants";
import type { JsonObject } from "../../../../../packages/shared-types/src/index.js";
import { useDocumentTitle } from "@/lib/hooks/use-document-title";
import { toastMutationError } from "@/lib/utils/formatting";
import type { GetApiTenantsTenantId200 } from "@/lib/api/generated/control-api.schemas";
import { useTenantContext } from "@/lib/hooks/use-tenant-context";

const editTenantSchema = z.object({
  name: z.string().min(1, "Name is required"),
  slug: z.string().optional(),
  status: z.enum(["draft", "active", "paused"]).default("draft"),
  timezone: z.string().optional(),
  branding: z.string().optional(),
  settings: z.string().optional(),
});

type EditTenantForm = z.infer<typeof editTenantSchema>;

export function EditTenantPage() {
  const { tenantId } = useTenantContext();
  const navigate = useNavigate();
  const { data: tenant, isLoading } = useGetApiTenantsTenantId<GetApiTenantsTenantId200>(
    tenantId!,
    { query: { enabled: Boolean(tenantId) } },
  );
  const mutation = usePatchApiTenantsTenantId();
  useDocumentTitle("Edit Tenant");

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<EditTenantForm>({
    resolver: zodResolver(editTenantSchema),
  });

  useEffect(() => {
    if (tenant) {
      reset({
        name: tenant.name,
        slug: tenant.slug ?? "",
        status: tenant.status ?? "draft",
        timezone: tenant.timezone ?? "",
        branding: tenant.branding ? JSON.stringify(tenant.branding, null, 2) : "",
        settings: tenant.settings ? JSON.stringify(tenant.settings, null, 2) : "",
      });
    }
  }, [tenant, reset]);

  const onSubmit = async (data: EditTenantForm) => {
    try {
      let branding: JsonObject | undefined;
      if (data.branding && data.branding.trim()) {
        try {
          branding = JSON.parse(data.branding);
        } catch {
          toast.error("Invalid JSON in branding field");
          return;
        }
      }

      let settings: JsonObject | undefined;
      if (data.settings && data.settings.trim()) {
        try {
          settings = JSON.parse(data.settings);
        } catch {
          toast.error("Invalid JSON in settings field");
          return;
        }
      }

      await mutation.mutateAsync({
        tenantId: tenantId!,
        data: {
          tenantId: tenantId!,
          name: data.name,
          slug: data.slug || undefined,
          status: data.status,
          timezone: data.timezone || undefined,
          branding,
          settings,
        },
      });
      toast.success(`Tenant "${data.name}" updated`);
      navigate("/workspace");
    } catch (err) {
      toast.error(toastMutationError(err, "Failed to update tenant"));
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-xl mx-auto">
        <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>Loading...</p>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto">
      <h1 className="text-lg font-semibold mb-6" style={{ color: "var(--text-strong)" }}>
        Edit Tenant
      </h1>
      <Card>
        <CardHeader>
          <CardTitle>Tenant Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <span className="text-xs font-medium" style={{ color: "var(--text-strong)" }}>Tenant ID</span>
            <p className="text-xs font-mono mt-1" style={{ color: "var(--muted-foreground)" }}>{tenantId}</p>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
            <FormField label="Name" error={errors.name?.message}>
              <input {...register("name")} placeholder="Acme Corp" className="form-input" />
            </FormField>
            <FormField label="Slug" error={errors.slug?.message}>
              <input {...register("slug")} placeholder="acme" className="form-input" />
            </FormField>
            <FormField label="Status">
              <select {...register("status")} className="form-input">
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
              </select>
            </FormField>
            <FormField label="Timezone">
              <input {...register("timezone")} placeholder="America/New_York" className="form-input" />
            </FormField>
            <FormField label="Branding (JSON)" error={errors.branding?.message}>
              <textarea
                {...register("branding")}
                placeholder='{ "logo": "https://..." }'
                className="form-input font-mono"
                rows={4}
              />
            </FormField>
            <FormField label="Settings (JSON)" error={errors.settings?.message}>
              <textarea
                {...register("settings")}
                placeholder='{ "key": "value" }'
                className="form-input font-mono"
                rows={4}
              />
            </FormField>

            <div className="flex items-center gap-3 pt-2">
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Saving..." : "Save Changes"}
              </Button>
              <Button type="button" variant="ghost" onClick={() => navigate("/workspace")}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function FormField({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium" style={{ color: "var(--text-strong)" }}>
        {label}
      </label>
      {children}
      {error && (
        <span className="text-xs" style={{ color: "var(--danger)" }}>
          {error}
        </span>
      )}
    </div>
  );
}
